/**
 * YouTube limits and the YouTube Optimizer trial caps. Browser-safe.
 */

/** YouTube's hard title limit, in characters. */
export const YOUTUBE_TITLE_MAX_CHARS = 100;
/** About how much of a title shows on mobile. */
export const YOUTUBE_TITLE_MOBILE_CHARS = 55;
/** YouTube counts the description limit in UTF-8 bytes, not characters. */
export const YOUTUBE_DESCRIPTION_MAX_BYTES = 5000;
/** About how much of a description shows above the fold. */
export const YOUTUBE_DESCRIPTION_FOLD_CHARS = 150;
/** Chapters render only with at least this many timestamps. */
export const YOUTUBE_CHAPTER_MIN_COUNT = 3;
/** Each chapter must be at least this long. */
export const YOUTUBE_CHAPTER_MIN_SEC = 10;

/** Trial caps. No credits are debited during the trial. */
export const VIDEO_OPTIMIZER_LIMITS = {
	/** Longest video the trial takes. */
	maxDurationSec: 2 * 60 * 60,
	/** Largest audio upload (the bucket enforces the same cap). */
	maxAudioBytes: 100 * 1024 * 1024,
	/** Runs working at once per organization. */
	maxInFlightPerOrg: 3,
	/** Runs per organization per rolling 7 days; failed runs don't count. */
	maxRunsPerWeek: 30,
	/** Check YouTube at most once per this many milliseconds per run. */
	checkYouTubeIntervalMs: 10 * 60 * 1000,
	/** An upload that never arrived expires after this long. */
	awaitingAudioTtlMs: 24 * 60 * 60 * 1000,
	/** A failed run keeps its audio this long so Retry can reuse it. */
	failedAudioRetentionMs: 24 * 60 * 60 * 1000,
	/** A run still working after this long is marked failed by the sweep. */
	stuckRunMs: 2 * 60 * 60 * 1000,
	/** Signed upload URLs live this long (seconds). */
	uploadUrlTtlSec: 60 * 60,
	/** Saved people and terms per property. */
	maxSavedPeople: 50,
	maxSavedTerms: 100,
	/** Extra terms on one run. */
	maxExtraTerms: 40,
} as const;

/**
 * What a run says when its stored audio is gone (a Retry after the 24-hour
 * retention): the file is the only source of audio, so the user chooses it
 * again.
 */
export const VIDEO_AUDIO_GONE_MESSAGE =
	"We no longer have this video's audio. Choose the file again to retry.";

/**
 * Audio types the bucket accepts. The browser uploads Opus in WebM (the
 * fallbacks are Opus at 48 kHz, then AAC in MP4). A few labels browsers use
 * for the same containers are allowed too.
 */
export const VIDEO_AUDIO_CONTENT_TYPES = [
	"audio/webm",
	"video/webm",
	"audio/ogg",
	"audio/mp4",
	"audio/x-m4a",
	"audio/aac",
	"audio/mpeg",
	"audio/wav",
	"audio/x-wav",
	"audio/flac",
] as const;

export type VideoAudioContentType = (typeof VIDEO_AUDIO_CONTENT_TYPES)[number];

/** Normalize a content type header (drop parameters such as `;codecs=opus`). */
export function normalizeAudioContentType(
	value: string | null | undefined,
): string {
	return (value ?? "").split(";")[0]?.trim().toLowerCase() ?? "";
}

export function isAllowedAudioContentType(
	value: string | null | undefined,
): value is VideoAudioContentType {
	return (VIDEO_AUDIO_CONTENT_TYPES as readonly string[]).includes(
		normalizeAudioContentType(value),
	);
}

/** File extension for the stored object key. */
export function audioExtensionFor(contentType: string): string {
	switch (normalizeAudioContentType(contentType)) {
		case "audio/webm":
		case "video/webm":
			return "webm";
		case "audio/ogg":
			return "ogg";
		case "audio/mp4":
		case "audio/x-m4a":
			return "m4a";
		case "audio/aac":
			return "aac";
		case "audio/mpeg":
			return "mp3";
		case "audio/wav":
		case "audio/x-wav":
			return "wav";
		case "audio/flac":
			return "flac";
		default:
			return "bin";
	}
}

/** `org/<orgId>/<optimizationId>/audio.<ext>`, always chosen by the server. */
export function videoAudioObjectKey(
	organizationId: string,
	optimizationId: string,
	contentType: string,
): string {
	return `org/${organizationId}/${optimizationId}/audio.${audioExtensionFor(contentType)}`;
}
